import { useState, useRef } from "react";
import { X, Loader2, Image as ImageIcon, Palette, Link as LinkIcon, Check } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import BannerCropper from "@/components/BannerCropper";
import { toast } from "sonner";

const ACCENTS = ["#4f7746", "#3f6238", "#1f6feb", "#8b5cf6", "#e11d48", "#ea580c", "#0891b2", "#111827"];
const LINK_FIELDS = [
  ["website", "Website", "https://yoursite.com"],
  ["youtube", "YouTube", "https://youtube.com/@you"],
  ["instagram", "Instagram", "https://instagram.com/you"],
  ["twitter", "X / Twitter", "https://x.com/you"],
  ["tiktok", "TikTok", "https://tiktok.com/@you"],
];

// Read a picked file into a data-URL, downscaled just enough to stay light for the cropper/upload.
const readImage = (file, maxW = 2400, quality = 0.92) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        if (scale === 1) { resolve(reader.result); return; }
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const CreatorPageEditModal = ({ open, onClose, page, onSaved }) => {
  const [displayName, setDisplayName] = useState(page?.display_name || "");
  const [bio, setBio] = useState(page?.bio || "");
  const [accent, setAccent] = useState(page?.accent_color || "#4f7746");
  const [links, setLinks] = useState(() => ({ website: "", youtube: "", instagram: "", twitter: "", tiktok: "", ...(page?.links || {}) }));
  const [bannerPreview, setBannerPreview] = useState(page?.banner_id ? imageUrl(page.banner_id) : null);
  const [bannerData, setBannerData] = useState(undefined); // undefined = unchanged, "" = clear, dataurl = new
  const [cropSrc, setCropSrc] = useState(null); // when set, the drag-to-frame cropper is showing
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  if (!open) return null;

  const pickBanner = async (e) => {
    const f = e.target.files?.[0];
    if (f) {
      try {
        setCropSrc(await readImage(f));
      } catch {
        toast.error("Couldn't read that image");
      }
    }
    e.target.value = ""; // allow re-picking the same file
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { display_name: displayName, bio, accent_color: accent, links };
      if (bannerData !== undefined) body.banner_image = bannerData;
      const { data } = await api.put("/me/creator-page", body);
      toast.success("Your page is updated ✨");
      onSaved?.(data.page);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your page");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose} data-testid="creator-edit-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden border border-[#dfe7d8] my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative px-6 py-5 text-white" style={{ background: `linear-gradient(135deg,${accent},#3f6238)` }}>
          <button data-testid="creator-edit-close" onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/15 transition-colors"><X size={20} /></button>
          <h3 className="text-lg font-extrabold leading-tight">Customize your channel</h3>
          <p className="text-[13px] font-semibold text-white/85 leading-tight">Make it yours — banner, bio, colour & links.</p>
        </div>

        <div className="px-6 py-6 space-y-5">
          {/* Banner */}
          <div>
            <label className="text-xs font-bold text-[#6b6880] flex items-center gap-1.5 mb-1.5"><ImageIcon size={13} /> Banner image</label>
            {cropSrc ? (
              <BannerCropper
                src={cropSrc}
                targetW={1600}
                targetH={400}
                onCancel={() => setCropSrc(null)}
                onApply={(dataUrl) => { setBannerData(dataUrl); setBannerPreview(dataUrl); setCropSrc(null); }}
              />
            ) : (
              <>
                <div className="w-full aspect-[4/1] rounded-2xl overflow-hidden border-2 border-dashed border-[#cfe0c7] bg-[#f7faf5] relative flex items-center justify-center">
                  {bannerPreview ? (
                    <img src={bannerPreview} alt="banner" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs text-[#9a96ac] px-4 text-center">Upload a photo that represents your channel</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button data-testid="creator-banner-upload" onClick={() => fileRef.current?.click()} className="text-xs font-bold text-[#3f6238] bg-[#eef3ea] px-3 py-1.5 rounded-full hover:bg-[#e2ecdc] transition-colors">
                    {bannerPreview ? "Change / reframe" : "Choose image"}
                  </button>
                  {bannerPreview && (
                    <button data-testid="creator-banner-remove" onClick={() => { setBannerData(""); setBannerPreview(null); }} className="text-xs font-bold text-[#b91c1c] px-2 py-1.5 rounded-full hover:bg-[#fee2e2] transition-colors">Remove</button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickBanner} />
                </div>
                <p className="text-[11px] text-[#9a96ac] mt-1.5 leading-snug">
                  Any photo works — you'll drag to frame it and we auto-crop the rest. For a pixel-perfect fit, use a <b className="text-[#6b6880]">1600 × 400 px</b> image (any wide 4:1 shot).
                </p>
              </>
            )}
          </div>

          {/* Display name */}
          <div>
            <label className="text-xs font-bold text-[#6b6880] mb-1.5 block">Channel name</label>
            <input data-testid="creator-display-name" value={displayName} maxLength={40} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Sushi School"
              className="w-full text-sm bg-white border-2 border-[#dbe4d3] rounded-xl px-3 py-2 outline-none focus:border-[#5d8a52] transition-colors" />
          </div>

          {/* Bio */}
          <div>
            <label className="text-xs font-bold text-[#6b6880] mb-1.5 block">Bio / tagline</label>
            <textarea data-testid="creator-bio" value={bio} maxLength={280} onChange={(e) => setBio(e.target.value)} rows={3} placeholder="Tell viewers what your channel is about…"
              className="w-full text-sm bg-white border-2 border-[#dbe4d3] rounded-xl px-3 py-2 outline-none focus:border-[#5d8a52] transition-colors resize-none" />
            <div className="text-[11px] text-[#b3aec6] text-right mt-0.5">{bio.length}/280</div>
          </div>

          {/* Accent */}
          <div>
            <label className="text-xs font-bold text-[#6b6880] flex items-center gap-1.5 mb-1.5"><Palette size={13} /> Accent colour</label>
            <div className="flex flex-wrap gap-2">
              {ACCENTS.map((c) => (
                <button key={c} data-testid={`creator-accent-${c}`} onClick={() => setAccent(c)} title={c}
                  className="w-8 h-8 rounded-full transition-transform hover:scale-110 flex items-center justify-center"
                  style={{ background: c, boxShadow: accent === c ? `0 0 0 3px #fff, 0 0 0 5px ${c}` : "none" }}>
                  {accent === c && <Check size={15} className="text-white" />}
                </button>
              ))}
            </div>
          </div>

          {/* Links */}
          <div>
            <label className="text-xs font-bold text-[#6b6880] flex items-center gap-1.5 mb-1.5"><LinkIcon size={13} /> Links</label>
            <div className="space-y-2">
              {LINK_FIELDS.map(([key, label, ph]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-[#6b6880] w-20 shrink-0">{label}</span>
                  <input data-testid={`creator-link-${key}`} value={links[key] || ""} onChange={(e) => setLinks({ ...links, [key]: e.target.value })} placeholder={ph}
                    className="flex-1 text-xs bg-white border border-[#dbe4d3] rounded-lg px-2.5 py-1.5 outline-none focus:border-[#5d8a52] transition-colors" />
                </div>
              ))}
            </div>
          </div>

          <button data-testid="creator-edit-save" onClick={save} disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 font-extrabold text-white py-3 rounded-2xl transition-colors disabled:opacity-70"
            style={{ background: `linear-gradient(135deg,${accent},#3f6238)` }}>
            {busy ? <><Loader2 size={18} className="spin" /> Saving…</> : "Save my channel"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatorPageEditModal;
